/*
 * The one way to (un)favourite a product from the storefront: auth gate,
 * optimistic store toggle, and a toast — "Saved · View wishlist" on add,
 * "Removed · Undo" on remove. Cards, the product page, quick view and the
 * wishlist page all call this; none of them talk to the favorites store
 * directly.
 */
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useFavoritesStore } from '../stores/favorites';
import { UNDO_MS, useToastStore } from '../stores/toast';
import { productCover, type ProductSummary } from '../types/product';

type FavoriteTarget = Pick<ProductSummary, 'id' | 'name' | 'coverUrl' | 'imageUrl'>;

export function useFavoriteToggle() {
  const auth = useAuthStore();
  const favorites = useFavoritesStore();
  const toast = useToastStore();
  const router = useRouter();
  const route = useRoute();
  const { t } = useI18n();

  function isFavorite(id: string): boolean {
    return favorites.isFavorite(id);
  }

  /** Returns the new state, or null when the call was redirected / failed. */
  async function toggle(product: FavoriteTarget): Promise<boolean | null> {
    if (!auth.isAuthenticated) {
      void router.push({ name: 'login', query: { next: route.fullPath } });
      return null;
    }
    try {
      const on = await favorites.toggle(product.id);
      const image = productCover(product);
      if (on) {
        toast.push({ title: t('common.toast.savedToWishlist'), body: product.name, image, action: { label: t('common.toast.viewWishlist'), to: '/favorites' } });
      } else {
        toast.push(
          {
            title: t('common.toast.removedFromWishlist'),
            body: product.name,
            image,
            tone: 'info',
            action: { label: t('common.toast.undo'), onClick: () => void favorites.toggle(product.id) },
          },
          UNDO_MS,
        );
      }
      return on;
    } catch {
      toast.push({ title: t('common.state.error'), tone: 'error' });
      return null;
    }
  }

  return { isFavorite, toggle };
}
